/**
 * Condition Upload Modal Component
 *
 * Modal for uploading documents to address a specific condition.
 * Features drag-drop upload, file validation, and progress tracking.
 */

import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, CheckCircle2, AlertCircle, Loader2, Lock } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import type { EncompassCondition } from '@/types/approval-letter';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('ConditionUploadModal', 'conditions-portal');

// API configuration
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// File limits
const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

interface ConditionUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  condition: EncompassCondition;
  /** Token for external (title/escrow) uploads */
  token?: string;
  /** Loan ID for internal (JWT-authenticated) uploads */
  loanId?: string;
  /** Whether this is an internal upload (uses JWT auth) */
  isInternal?: boolean;
  /** JWT for internal uploads (passed from parent) */
  jwt?: string;
  onSuccess: (uploadId: string, confirmationNumber: string) => void;
  /** File lock status (optional to avoid breaking existing usages/mocks) */
  isFileLocked?: boolean;
  fileLockedByName?: string | null;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

interface FileUpload {
  file: File;
  state: UploadState;
  progress: number;
  error?: string;
  confirmationNumber?: string;
}

export const ConditionUploadModal = ({
  open,
  onOpenChange,
  condition,
  token,
  loanId,
  isInternal = false,
  jwt,
  onSuccess,
  isFileLocked = false,
  fileLockedByName = null
}: ConditionUploadModalProps) => {
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Validate file type and size
   */
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return { valid: false, error: 'Only PDF, JPG, and PNG files are allowed' };
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: 'File size must be under 25MB' };
    }
    return { valid: true };
  };

  /**
   * Handle file drop
   */
  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newUploads: FileUpload[] = acceptedFiles.map(file => {
      const validation = validateFile(file);
      return {
        file,
        state: validation.valid ? 'idle' : 'error',
        progress: 0,
        error: validation.error
      };
    });

    setUploads(prev => [...prev, ...newUploads]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    maxSize: MAX_FILE_SIZE,
    multiple: true
  });

  /**
   * Remove a file from the upload list
   */
  const removeFile = (index: number) => {
    setUploads(prev => prev.filter((_, i) => i !== index));
  };

  /**
   * Upload a single file
   */
  const uploadFile = async (index: number): Promise<void> => {
    const upload = uploads[index];
    if (!upload || upload.state !== 'idle') {
return;
}

    setUploads(prev => prev.map((u, i) =>
      i === index ? { ...u, state: 'uploading', progress: 10 } : u
    ));

    try {
      const formData = new FormData();
      const conditionId = condition.externalId || condition.id || condition.internalId;
      formData.append('externalId', conditionId);
      formData.append('conditionName', condition.title);
      formData.append('file', upload.file);

      // Add auth-specific fields
      if (isInternal && loanId) {
        formData.append('loanId', loanId);
      } else if (token) {
        formData.append('token', token);
      }

      // Simulate progress
      setUploads(prev => prev.map((u, i) =>
        i === index ? { ...u, progress: 30 } : u
      ));

      // Build headers based on auth mode
      const headers: Record<string, string> = {
        apikey: SUPABASE_ANON_KEY
      };
      if (isInternal && jwt) {
        headers['Authorization'] = `Bearer ${jwt}`;
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/conditions-portal-upload`, {
        method: 'POST',
        headers,
        body: formData
      });

      setUploads(prev => prev.map((u, i) =>
        i === index ? { ...u, progress: 70 } : u
      ));

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      setUploads(prev => prev.map((u, i) =>
        i === index ? {
          ...u,
          state: 'success',
          progress: 100,
          confirmationNumber: result.confirmationNumber
        } : u
      ));

      log.info('File uploaded', {
        filename: upload.file.name,
        confirmationNumber: result.confirmationNumber
      });

    } catch (error) {
      log.error('Upload error', { filename: upload.file.name, error });
      setUploads(prev => prev.map((u, i) =>
        i === index ? {
          ...u,
          state: 'error',
          progress: 0,
          error: error instanceof Error ? error.message : 'Upload failed'
        } : u
      ));
    }
  };

  /**
   * Upload all pending files
   */
  const uploadAllFiles = async () => {
    const pendingIndices = uploads
      .map((u, i) => ({ upload: u, index: i }))
      .filter(({ upload }) => upload.state === 'idle')
      .map(({ index }) => index);

    if (pendingIndices.length === 0) {
return;
}

    setIsUploading(true);

    // Track successful uploads during the loop
    const lastConfirmationNumber: string | null = null;

    for (const index of pendingIndices) {
      await uploadFile(index);
    }

    setIsUploading(false);

    // Use setState callback to access latest state after all uploads
    setUploads(currentUploads => {
      const successfulUpload = currentUploads.find(u => u.state === 'success' && u.confirmationNumber);
      if (successfulUpload?.confirmationNumber) {
        // Defer to avoid setState during render
        setTimeout(() => {
          onSuccess('', successfulUpload.confirmationNumber!);
        }, 0);
      }
      return currentUploads;
    });
  };

  /**
   * Handle close
   */
  const handleClose = () => {
    if (!isUploading) {
      setUploads([]);
      onOpenChange(false);
    }
  };

  const hasValidFiles = uploads.some(u => u.state === 'idle');
  const hasSuccessfulUploads = uploads.some(u => u.state === 'success');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            Upload a document for: <strong>{condition.title}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* File Lock Warning */}
          {isFileLocked && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-amber-600 shrink-0" />
                <p className="text-sm text-amber-800">
                  <strong>{fileLockedByName || 'Someone'}</strong> has this loan open. Your upload will be saved, but may not sync to Encompass yet.
                </p>
              </div>
            </div>
          )}

          {/* Dropzone */}
          <div
            {...getRootProps()}
            role="button"
            tabIndex={0}
            aria-label="Upload area. Drop files here or click to browse. Accepts PDF, JPG, and PNG files up to 25MB."
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors
              ${isDragActive
                ? 'border-cyan-500 bg-cyan-50'
                : 'border-gray-300 hover:border-cyan-400 hover:bg-gray-50'
              }
              ${isUploading ? 'pointer-events-none opacity-50' : ''}
            `}
          >
            <input {...getInputProps()} />
            <Upload className="h-10 w-10 mx-auto text-gray-400 mb-3" />
            {isDragActive ? (
              <p className="text-cyan-600 font-medium">Drop files here...</p>
            ) : (
              <>
                <p className="text-gray-600">
                  Drag & drop files here, or <span className="text-cyan-600 font-medium">browse</span>
                </p>
                <p className="text-sm text-gray-400 mt-1">
                  PDF, JPG, or PNG (max 25MB)
                </p>
              </>
            )}
          </div>

          {/* File List */}
          {uploads.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {uploads.map((upload, index) => (
                <div
                  key={index}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg border
                    ${upload.state === 'error' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}
                    ${upload.state === 'success' ? 'bg-green-50 border-green-200' : ''}
                  `}
                >
                  <FileText className="h-5 w-5 text-gray-400 shrink-0" />

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {upload.file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(upload.file.size / 1024 / 1024).toFixed(2)} MB
                    </p>

                    {upload.state === 'uploading' && (
                      <Progress value={upload.progress} className="h-1 mt-1" />
                    )}

                    {upload.state === 'error' && (
                      <p className="text-xs text-red-600 mt-1">{upload.error}</p>
                    )}

                    {upload.state === 'success' && upload.confirmationNumber && (
                      <p className="text-xs text-green-600 mt-1">
                        Confirmation: {upload.confirmationNumber}
                      </p>
                    )}
                  </div>

                  {/* Status/Actions */}
                  <div className="shrink-0">
                    {upload.state === 'idle' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                        disabled={isUploading}
                        aria-label={`Remove ${upload.file.name}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    {upload.state === 'uploading' && (
                      <Loader2 className="h-5 w-5 text-cyan-500 animate-spin" />
                    )}
                    {upload.state === 'success' && (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    )}
                    {upload.state === 'error' && (
                      <AlertCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={handleClose} disabled={isUploading}>
              {hasSuccessfulUploads ? 'Done' : 'Cancel'}
            </Button>
            {hasValidFiles && (
              <Button
                onClick={uploadAllFiles}
                disabled={isUploading}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload {uploads.filter(u => u.state === 'idle').length} File{uploads.filter(u => u.state === 'idle').length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConditionUploadModal;
